// Rapprochement des inscrits d'un webinaire avec les prospects du CRM.
// Module pur : aucune dépendance hors `welya`, utilisable côté worker comme côté front.

import { segmentOf, type Segment, type WelyaRow } from "./welya";

export type MatchMethod = "email" | "phone" | "name" | "new" | "excluded";

export interface ProspectLite {
  id: number;
  firstName: string;
  lastName: string | null;
  email: string | null;
  phone: string | null;
  /** YYYY-MM-DD des google_events confirmés */
  rdvDates: string[];
}

export interface MatchResult {
  row: WelyaRow;
  segment: Segment;
  method: MatchMethod;
  prospectId: number | null;
  needsConfirmation: boolean;
  possibleDuplicateOf: number | null;
  /**
   * Index dans `rows` de la PREMIÈRE ligne ayant le même e-mail (normalisé), null pour
   * cette première ligne elle-même. Une ligne répétée recopie method, prospectId,
   * needsConfirmation et possibleDuplicateOf de la première et ne consomme aucune autre fiche.
   */
  duplicateRowOf: number | null;
}

/** Fenêtre de rapprochement par prénom : du jour du live à +15 jours inclus. */
const NAME_WINDOW_DAYS = 15;

export function normalizeEmail(e: string): string {
  return e.trim().toLowerCase();
}

/** 9 derniers chiffres, null s'il y en a moins de 9. */
export function phoneKey(p: string | null): string | null {
  if (p === null) return null;
  const digits = p.replace(/\D/g, "");
  return digits.length < 9 ? null : digits.slice(-9);
}

/** Réglage `owner_emails` : entrées séparées par `,`, `;` ou un saut de ligne, vides écartées. */
export function parseOwnerEmails(value: string): string[] {
  return value
    .split(/[,;\n]/)
    .map((s) => s.trim())
    .filter((s) => s !== "");
}

/** Une entrée `@domaine.com` couvre tout le domaine ; sinon adresse exacte. */
export function isOwnerEmail(email: string, ownerEmails: string[]): boolean {
  const e = normalizeEmail(email);
  return ownerEmails.some((entry) => {
    const o = normalizeEmail(entry);
    if (o === "") return false;
    return o.startsWith("@") ? e.endsWith(o) : e === o;
  });
}

/**
 * Vrai si le « prénom » est en fait un bout d'adresse : égal à la partie avant `@`
 * (insensible à la casse), ou contenant `.`, `_` ou un chiffre.
 */
export function looksLikeEmailPrefix(firstName: string, email: string | null): boolean {
  const name = firstName.trim();
  if (name === "") return false;
  if (/[._\d]/.test(name)) return true;
  if (email === null) return false;
  const at = email.indexOf("@");
  const prefix = (at === -1 ? email : email.slice(0, at)).trim().toLowerCase();
  return prefix !== "" && prefix === name.toLowerCase();
}

/** Premier mot, sans accents ni casse. */
function firstNameKey(s: string): string {
  return fold(s.trim().split(/\s+/)[0] ?? "");
}

function fold(s: string): string {
  return s
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
}

/** Prénom complet de l'inscrit, sans accents, casse, `-` ni espaces (« Anne-Soline » → « annesoline »). */
function registrantKey(s: string): string {
  return fold(s.trim()).replace(/[-\s]/g, "");
}

/** Partie locale de l'e-mail, sans accents, casse, `.`, `_`, `-` ni `+`. */
function emailLocalKey(email: string): string {
  const at = email.indexOf("@");
  return fold((at === -1 ? email : email.slice(0, at)).trim()).replace(/[._\-+]/g, "");
}

/** Clé d'inscrit trop courte en dessous : « Lo » ne doit pas rapprocher « lomartin@… ». */
const MIN_PREFIX_KEY = 3;

/**
 * Prénom CRM saisi comme un bout d'adresse (`delphinemartin`) : correspondance si la partie
 * locale de l'e-mail de la fiche commence par le prénom de l'inscrit.
 */
function emailPrefixMatches(c: ProspectLite, registrantFirstName: string): boolean {
  if (c.email === null || !looksLikeEmailPrefix(c.firstName, c.email)) return false;
  const key = registrantKey(registrantFirstName);
  return key.length >= MIN_PREFIX_KEY && emailLocalKey(c.email).startsWith(key);
}

function addDays(isoDate: string, days: number): string | null {
  const m = isoDate.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  const t = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])) + days * 86_400_000;
  return new Date(t).toISOString().slice(0, 10);
}

export function matchAttendees(
  rows: WelyaRow[],
  prospects: ProspectLite[],
  opts: { ownerEmails: string[]; heldAt: string },
): MatchResult[] {
  // Résultat déterministe si la base contient deux fiches au même e-mail/téléphone :
  // le plus petit id gagne.
  const sorted = [...prospects].sort((a, b) => a.id - b.id);
  const heldAt = opts.heldAt.slice(0, 10);

  const firstByEmail = new Map<string, number>();
  const results: MatchResult[] = rows.map((row, i) => {
    const key = normalizeEmail(row.email);
    const first = firstByEmail.get(key);
    if (first === undefined) firstByEmail.set(key, i);
    return {
      row,
      segment: segmentOf(row),
      method: "new",
      prospectId: null,
      needsConfirmation: false,
      possibleDuplicateOf: null,
      duplicateRowOf: first ?? null,
    };
  });
  const taken = new Set<number>();
  const pending = new Set<number>(); // indices des premières lignes encore à rapprocher

  results.forEach((res, i) => {
    if (res.duplicateRowOf !== null) return; // recopiée à la fin
    if (isOwnerEmail(res.row.email, opts.ownerEmails)) res.method = "excluded";
    else pending.add(i);
  });

  const settle = (i: number, method: MatchMethod, prospectId: number) => {
    const res = results[i];
    res.method = method;
    res.prospectId = prospectId;
    res.needsConfirmation = method === "name";
    taken.add(prospectId);
    pending.delete(i);
  };

  // Passes sûres d'abord (e-mail, puis téléphone) pour tout le monde, afin qu'un
  // rapprochement par prénom plus haut dans la liste ne vole pas une fiche certaine.
  for (const i of [...pending]) {
    const email = normalizeEmail(results[i].row.email);
    const p = sorted.find(
      (c) => !taken.has(c.id) && c.email !== null && normalizeEmail(c.email) === email,
    );
    if (p) settle(i, "email", p.id);
  }

  for (const i of [...pending]) {
    const key = phoneKey(results[i].row.phone);
    if (key === null) continue;
    const p = sorted.find((c) => !taken.has(c.id) && phoneKey(c.phone) === key);
    if (p) settle(i, "phone", p.id);
  }

  const windowEnd = addDays(heldAt, NAME_WINDOW_DAYS);
  for (const i of [...pending]) {
    const key = firstNameKey(results[i].row.firstName);
    if (key === "" || windowEnd === null) continue;
    const candidates = sorted.filter(
      (c) =>
        !taken.has(c.id) &&
        (firstNameKey(c.firstName) === key ||
          emailPrefixMatches(c, results[i].row.firstName)) &&
        c.rdvDates.some((d) => {
          const day = d.slice(0, 10);
          return day >= heldAt && day <= windowEnd;
        }),
    );
    if (candidates.length === 1) {
      settle(i, "name", candidates[0].id);
    } else if (candidates.length > 1) {
      results[i].possibleDuplicateOf = Math.min(...candidates.map((c) => c.id));
    }
  }

  for (const res of results) {
    if (res.duplicateRowOf === null) continue;
    const first = results[res.duplicateRowOf];
    res.method = first.method;
    res.prospectId = first.prospectId;
    res.needsConfirmation = first.needsConfirmation;
    res.possibleDuplicateOf = first.possibleDuplicateOf;
  }

  return results;
}
